import React, {useCallback, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {pluralize} from '@pb/kit-formatters';
import {useGateValue} from '@pb/kit-lib-statsig';

import {MFA_SETTINGS_MODAL_TITLES} from '../constants';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {UpdateMfaRequest} from 'src/openapi/admin/models/UpdateMfaRequest';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    usersIds: number[],
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UpdateMfaRequest>,
    updateStatus: boolean,
};

export const UpdateMfaSettingsModal = ({usersIds, onCloseModal, processAction, updateStatus}: Props) => {
    const emailPrimaryMfaEnabled = useGateValue(STATSIG_GATES.EMAIL_PRIMARY_MFA);
    const [mfaSelected, setMfaSelected] = useState(false);
    const [emailPrimaryFactorSelected, setEmailPrimaryFactorSelected] = useState(false);
    const caption = updateStatus
        ? MFA_SETTINGS_MODAL_TITLES.ENABLE_MFA_SETTINGS
        : MFA_SETTINGS_MODAL_TITLES.DISABLE_MFA_SETTINGS;
    const onMfaChange = useCallback((changeData: CheckBoxChangeData) => {
        const selected = changeData.event.currentTarget.checked;
        setMfaSelected(selected);
        if (updateStatus) {
            setEmailPrimaryFactorSelected(selected ? emailPrimaryFactorSelected : false);
        } else if (selected) {
            setEmailPrimaryFactorSelected(true);
        }
    }, [emailPrimaryFactorSelected, updateStatus]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {caption}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="h">
                    <Group>
                        {pluralize(
                            `Are you sure you want to ${caption} for ${usersIds.length} user[s]?`,
                            usersIds.length,
                        )}
                    </Group>
                </Group>
                {emailPrimaryMfaEnabled ? (
                    <div style={{marginTop: 16}}>
                        <CheckBox
                            name="mfaStatus"
                            label="Multi-Factor Authentication (MFA)"
                            checked={mfaSelected}
                            onCheckBoxChange={onMfaChange}
                        />
                        <CheckBox
                            disabled={updateStatus ? !mfaSelected : mfaSelected}
                            name="emailPrimaryFactorStatus"
                            label="Email as Primary MFA"
                            checked={emailPrimaryFactorSelected}
                            onCheckBoxChange={(changeData: CheckBoxChangeData) => (
                                setEmailPrimaryFactorSelected(changeData.event.currentTarget.checked)
                            )}
                        />
                    </div>
                ) : null}
            </DialogMain>
            <BulkActionFooter
                action="Update"
                onConfirm={async (options?: ActionOptions) => {
                    return await processAction(emailPrimaryMfaEnabled ? {
                        usersIds,
                        ...(mfaSelected ? {mfaStatus: updateStatus} : {}),
                        ...(emailPrimaryFactorSelected
                            ? {emailPrimaryFactorStatus: updateStatus}
                            : {}),
                    } : {usersIds, mfaStatus: updateStatus}, options);
                }}
                disabled={emailPrimaryMfaEnabled ? !mfaSelected && !emailPrimaryFactorSelected : false}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
